import Head from 'next/head'
import { getRequestJSON } from '../../../api/index/location-api';
import Script from 'next/script'
import { Aircon } from '../../../components/index/service-category/aircon-service/Aircon';
import { getArticleDateTime, reviewedIso } from '../../../utils/article-date-time';
import { useEffect } from 'react';
import { airconCostCalculation } from '../../../js/aircon-cost';
import { getTestimonials } from 'api/user/profile-api';
import { getAcTestimonials, setAcTestimonials } from 'service/testimonial-store';
import { localBusinessNode, buildServiceNode } from 'utils/local-business-schema'
import { buildAirconOfferCatalog } from 'utils/aircon-offers'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-22'

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
export async function getStaticProps() {
  const { month, year } = getArticleDateTime();
  const modifiedDate = reviewedIso(CONTENT_REVIEWED);

  const countryCd = "sg"
  const countryName = "Singapore"
  
  const allRequestCategorySettings = await getRequestJSON(countryName)

  const testimonials = getAcTestimonials().length > 0 ? getAcTestimonials() : await getTestimonials("Aircon Services", 0, 6)
  setAcTestimonials(testimonials)

  const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Aircon Services")
  
  const currencyCd = "SGD"

  let standardServicing = []
  let annualServicing = []
  let chemicalServicing = []
  let overhaulServicing = []    

  airconCostCalculation(currencyCd, annualServicing, standardServicing, chemicalServicing, overhaulServicing)

  return {
      props: {
        requestCategorySettings,
        countryName,
        countryCd,
        currencyCd,
        standardServicing,
        chemicalServicing,
        overhaulServicing,
        modifiedDate,
        month,
        year,
        testimonials
      },
      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 259200, // In seconds
  }
}

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// the path has not been generated.
// export async function getStaticPaths() {

//     // Get the paths we want to pre-render based on posts
//     const paths = countryList.map((country) => ({
//       params: { countryCd: country.country_cd },
//     }))

//     // We'll pre-render only these paths at build time.
//     // { fallback: blocking } will server-render pages
//     // on-demand if the path doesn't exist.
//     return { paths, fallback: 'blocking' }
// }

// One description for meta, og and twitter. It previously called StringsSG
// "the only aircon servicing provider" in Singapore - an unverifiable
// superlative. Prices match the offers in the JSON-LD below.
const pageDescription = 'Aircon servicing in Singapore with transport included: general service from $47.84, chemical wash from $82.80, plus chemical overhaul, repair and gas top up. 30 to 60 day warranty.'

export default function Home(props) {
  const requestCategorySettings = props.requestCategorySettings
  const countryName = props.countryName
  const countryCd = props.countryCd
  const currencyCd = props.currencyCd
  const standardServicing = props.standardServicing
  const chemicalServicing = props.chemicalServicing
  const overhaulServicing = props.overhaulServicing
  const modifiedDate = props.modifiedDate
  const month = props.month
  const year = props.year
  const testimonials = props.testimonials

  useEffect(() => {
      const currentLocation = globalThis.location.href;    
      if (currentLocation.includes("#")){
          let locationId = currentLocation.substring(currentLocation.indexOf("#") + 1);
          let ele = document.getElementById(locationId); 
          if(ele){
              setTimeout(() => {
                  ele.scrollIntoView({ behavior: "smooth", block:"start"});
              }, 500)
          }     
      }
  }, []) 

  return (
    <>
      <Head>
        <title>Aircon Servicing Singapore from $16/unit | StringsSG</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description"
            content={pageDescription}/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification"
            content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:locale:alternate" content="en_US" />
        <meta property="og:site_name" content="StringsSG" />
        <meta property="og:url"  content={`https://www.stringssg.com/sg/aircon-servicing-singapore`} />
        <meta property="og:type"   content="website" />
        <meta property="og:title"    content={`Aircon Servicing Singapore from $16/unit | StringsSG`}/>
        <meta property="og:description" content={pageDescription}/>
        <meta property="og:image"  content="https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/blog/living/singapore-aircon-technician.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content={`https://www.stringssg.com/sg/aircon-servicing-singapore`}/>
        <meta name="twitter:title" content={`Aircon Servicing Singapore from $16/unit | StringsSG`}/> 
        <meta name="twitter:description" content={pageDescription}/> 
        <meta name="twitter:image" content="https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/blog/living/singapore-aircon-technician.png" /><link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@type":"BreadcrumbList","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/#breadcrumblist","itemListElement":[{"@type":"ListItem","position":1,"item":{"@id":"https://www.stringssg.com/","@type":"WebPage","name":"Home"}},{"@type":"ListItem","position":2,"item":{"@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/","@type":"WebPage","name":"Aircon Servicing Singapore"}}]},${JSON.stringify(localBusinessNode)},{"@type":"Service","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/#service","name":"Aircon Servicing Singapore","serviceType":"Aircon Servicing","description":"Aircon servicing in Singapore with transport included in every price. General servicing, chemical wash, chemical overhaul, repair, compressor cleaning and refrigerant gas top up for HDB, BTO, condominium and commercial units.","url":"https://www.stringssg.com/sg/aircon-servicing-singapore","provider":{"@id":"https://www.stringssg.com/#localbusiness"},"areaServed":{"@type":"Country","name":"Singapore"},"hasOfferCatalog":${JSON.stringify(buildAirconOfferCatalog({ name: 'Aircon Services and Prices in Singapore', standardServicing, chemicalServicing, overhaulServicing, includeCompressor: true }))}},{"@type":"VideoObject","@id":"https://www.stringssg.com/sg/aircon-servicing-singapore/#video","name":"Steps Involved in Aircon Servicing in Singapore","description":"Step-by-step demonstration of professional aircon servicing in Singapore by StringsSG.","thumbnailUrl":["https://stringssg-assets.s3.ap-southeast-1.amazonaws.com/static/blog/living/aircon/chinese-aircon-service-man-smiling.jpg"],"uploadDate":"2025-02-03T08:00:00+08:00","duration":"PT2M0S","embedUrl":"https://www.youtube.com/embed/Pa5-iT9WkV8"},{"@type":"ItemList","name":"Aircon Services Menu","itemListOrder":"Unordered","itemListElement":[{"@type":"ListItem","position":1,"name":"Aircon Leaking Water","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-leaking-water"},{"@type":"ListItem","position":2,"name":"Aircon Is Not Cooling","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-not-cooling"},{"@type":"ListItem","position":3,"name":"Aircon Doesn’t Turn On","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/do-this-when-your-aircon-stops-working-suddenly"},{"@type":"ListItem","position":4,"name":"Aircon Overheating","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-overheating"},{"@type":"ListItem","position":5,"name":"Aircon Gas Leakage","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-gas-leakage"},{"@type":"ListItem","position":6,"name":"Aircon General Service","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/cleaning-maintenance-service"},{"@type":"ListItem","position":7,"name":"Aircon Chemical Wash & Overhaul","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-chemical-wash-and-overhaul"},{"@type":"ListItem","position":8,"name":"Aircon Repair","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-repair"},{"@type":"ListItem","position":9,"name":"Urgent Aircon Servicing","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/urgent-aircon-servicing"},{"@type":"ListItem","position":10,"name":"Aircon Condenser Cleaning","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-condenser-cleaning"},{"@type":"ListItem","position":11,"name":"Aircon Gas Top Up","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-gas-top-up"},{"@type":"ListItem","position":12,"name":"Aircon Installation","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-installation"},{"@type":"ListItem","position":13,"name":"Aircon Parts Replacement","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-parts-replacement"},{"@type":"ListItem","position":14,"name":"Annual Package Service","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/annual-package"},{"@type":"ListItem","position":15,"name":"Cassette Aircon Servicing","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/ceiling-cassette-aircon-service"},{"@type":"ListItem","position":16,"name":"Aircon Servicing Pricing Page","url":"https://www.stringssg.com/sg/aircon-servicing-singapore/aircon-servicing-price-singapore"}]}]}`
          }}
        />
        <link rel="canonical" href="https://www.stringssg.com/sg/aircon-servicing-singapore"/>
      </Head>   
        {/* _app.js already wraps every page in <main className="pt-14">, so a second
            <main> here nested two landmarks inside each other. */}
        <Aircon requestCategorySettings={requestCategorySettings} countryName={countryName} oneTimeServicing={standardServicing}
        chemicalServicing={chemicalServicing} overhaulServicing={overhaulServicing}
        currencyCd={currencyCd} year={year} month={month} testimonials={testimonials}/>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
        />
        {/* The Facebook pixel that used to be duplicated here is already mounted
            globally in _app.js with the same id and the same pixel, so this copy only
            re-fired init/PageView. */}
    </>
  )
}